import _                                                                  from 'underscore';
import React, { FC, useCallback, useContext, useEffect, useState }        from 'react';
import { useQuery }                                                       from '@apollo/react-hooks';
import { Button, Header, Icon, Label, List, Popup, PopupProps, Progress } from 'semantic-ui-react';
import { Cell, Grid }                                                     from 'styled-css-grid';

import { Align, CorvalProjectSubObjective, SemanticUIColor, Size, SystemEvent, Uuid } from '../../../../../types';

import { ObjectiveProgressionDetails, ProjectActivityStatusChangeCallback, useProjectObjectives } from '../../../../../hooks';
import { DecisionEngineQueries }                                                                  from '../../../../../queries';
import { queriesHaveData, queriesLoading }                                                        from '../../../../../store/_apolloUtil';
import { ACTIVITY_STATUS, ACTIVITY_STATUS_INFO, OBJECTIVE_STATUS }                                from '../../../../../constants/ProductEnums';
import { runIfFunction }                                                                          from '../../../../../util/FunctionalUtility';
import { DynamicComponentsContext }                                                               from '../../../../contexts';
import { CriticalityHarveyBall, HarveyBall, HarveyBallProps, InactiveHarveyBall }                 from '../../../../components/util/visual/HarveyBall';
import { StyledContainer }                                                                        from '../../../../containers';
import { ProjectContext }                                                                         from '../../ProjectIndexPage';
import { ObjectiveProgressionUpdateDialog, ObjectiveProgressionUpdateDialogProps }                from './ObjectiveProgressionUpdateDialog';
import { getOffScreenNodeElement }                                                                from '../../../../containers/OffScreenNode';
import { WatchQueryFetchPolicy }                                                                  from 'apollo-client';
import { maybeStaticVersionField }                                                                from "@src/util";


interface Props {
    id: Uuid,
    allowUpdate?: boolean,
    size?: HarveyBallProps['size'],
    completedIconProps?: HarveyBallProps['completedIconProps'],
    popup?: Omit<PopupProps, 'trigger'>,
    indicatorComponent?: ( progress: ObjectiveProgressionDetails, isLoading: boolean, onClick: () => void ) => any,
    appendedComponent?: ( progress: ObjectiveProgressionDetails, isLoading: boolean, onClick: () => void ) => any,
    showPopupHeader?: boolean,
    showPercentageComplete?: boolean,
    onStatusChange?: ProjectActivityStatusChangeCallback,
    fetchPolicy?: WatchQueryFetchPolicy
}

interface PassThroughComponentProps {
    progress: ObjectiveProgressionDetails,
    isLoading: boolean,

    onClick(): void,
}

const defaultProps: Props = {
    id:                     null,
    allowUpdate:            true,
    size:                   HarveyBall.defaultProps.size,
    completedIconProps:     {
        name:     'check',
        size:     null,
        fitted:   true,
        inverted: true,
    },
    popup:                  null,
    indicatorComponent:     null,
    appendedComponent:      null,
    showPopupHeader:        false,
    showPercentageComplete: false,
    fetchPolicy:            null,
}

const ObjectiveProgressionIndicator: FC<Props> = ( props ) => {

    let { projectId, organizationId, isCorvalManaged, projectVersionId } = useContext( ProjectContext ),
        { modal: [ modal, modalActions ] }                               = useContext( DynamicComponentsContext ),
        [ projectSubObjective, setProjectSubObjective ]                  = useState<CorvalProjectSubObjective>( null ),
        [ progress, setProgress ]                                        = useState<ObjectiveProgressionDetails>( null );

    let {
            generateObjectiveProgressStatus,
            getObjectiveCompletionPercentage,
            getObjectiveCompletionOutOfString,
            getObjectiveStatusCountStatement,
            getObjectiveCompletionPercentageString,
            getObjectiveStatusHarveyBallSectors,
            isObjectiveCompletelyDeferred,
        } = useProjectObjectives( projectId )

    let getObjectiveProgressReq = useQuery<
        { projectSubObjectives: CorvalProjectSubObjective[] } |
        { staticProjectSubObjectives: CorvalStaticProjectSubObjective[] }
    >(
        DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVE_PROGRESS( isCorvalManaged ? null : projectVersionId ), {
            variables:   {
                projectId,
                projectVersionId,
                where: {
                    subObjectiveId: {
                        eq: props.id
                    }
                }
            },
            fetchPolicy: props.fetchPolicy ?? 'network-only',
        } );

    let isLoading = queriesLoading( getObjectiveProgressReq )
                    || _.isNull( projectSubObjective )
                    || _.isNull( progress );

    useEffect( () => {

        if( !queriesLoading( getObjectiveProgressReq ) &&
            queriesHaveData( getObjectiveProgressReq ) ){

            const projectSubObjectivesField = maybeStaticVersionField( 'projectSubObjectives', !isCorvalManaged ),
                  projectActivitiesField    = maybeStaticVersionField( 'projectActivities', !isCorvalManaged );

            let projectSubObjective = _.first( getObjectiveProgressReq.data[ projectSubObjectivesField ] ) ?? null;

            if( projectSubObjective ){

                let projectActivities = projectSubObjective[ projectActivitiesField ];

                // aggregate statuses
                let objectiveProgress = generateObjectiveProgressStatus( projectActivities as any );

                //console.log( objectiveProgress );
                setProjectSubObjective( projectSubObjective );
                setProgress( objectiveProgress );

            }
        }

    }, [ getObjectiveProgressReq ] );

    const handleOpenObjectiveProgressionDialog = ( e: SystemEvent ) => {

        // make the popup trigger to close by tapping the dom
        // document.querySelector( 'body' ).click();
        getOffScreenNodeElement().click();

        // Open the dialog.
        modalActions.show(
            <ObjectiveProgressionUpdateDialog
                id={ props.id }
                projectId={ projectId }
                organizationId={ organizationId }
                onUpdateComplete={ handleProgressUpdated }
                onUpdateCanceled={ handleProgressAbandoned }
                onUpdateError={ handleProgressError }
            />, {
                usePortal:            true,
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
                centered:             true,
                size:                 'large',
            } );
    }

    const handleProgressUpdated: ObjectiveProgressionUpdateDialogProps['onUpdateComplete'] = React.useCallback( async ( deltas ) => {

        runIfFunction( props.onStatusChange, deltas );

    }, [ getObjectiveProgressReq ] );

    const handleProgressAbandoned: ObjectiveProgressionUpdateDialogProps['onUpdateCanceled'] = React.useCallback( async ( e: SystemEvent ) => {

        // ...

    }, [ getObjectiveProgressReq ] );

    const handleProgressError: ObjectiveProgressionUpdateDialogProps['onUpdateError'] = React.useCallback( async () => {

        // ...

    }, [ getObjectiveProgressReq ] );

    const getPopupContent = useCallback( () => {

        if( !progress ) return null;

        let reportItems = [
            {
                value: progress.activities.completed,
                desc:  getObjectiveStatusCountStatement( progress, ACTIVITY_STATUS.COMPLETE, false ),
                color: ACTIVITY_STATUS_INFO.COMPLETE.COLOR,
                show:  progress.status !== OBJECTIVE_STATUS.COMPLETE,
            },
            {
                value: progress.activities.inProgress,
                desc:  getObjectiveStatusCountStatement( progress, ACTIVITY_STATUS.INCOMPLETE, false ),
                color: ACTIVITY_STATUS_INFO.INCOMPLETE.COLOR,
                show:  progress.status !== OBJECTIVE_STATUS.COMPLETE,
            },
            {
                value: progress.activities.notStarted,
                desc:  getObjectiveStatusCountStatement( progress, ACTIVITY_STATUS.NOTSTARTED, false ),
                color: ACTIVITY_STATUS_INFO.NOTSTARTED.COLOR,
                show:  progress.activities.notStarted > 0,
            },
            {
                value: progress.activities.willNotDo,
                desc:  getObjectiveStatusCountStatement( progress, ACTIVITY_STATUS.EXCLUDED, false ),
                color: ACTIVITY_STATUS_INFO.EXCLUDED.COLOR,
                show:  progress.activities.willNotDo > 0,
            },
        ];

        let reportableItems       = _.filter( reportItems, 'show' ),
            isComplete            = progress.status === OBJECTIVE_STATUS.COMPLETE,
            allActivitiesDeferred = progress.activities.willNotDo === progress.activities.total,
            hasDeferredActivities = ( progress.activities.willNotDo > 0 );

        return (
            <div style={ { maxWidth: 320 } }>

                { props.showPopupHeader && (
                    <Header
                        as={ 'h5' }
                        textAlign={ 'left' }
                        icon={ { name: 'bullseye', size: 'tiny', color: 'teal' } }
                        className={ 'margin-xs-bottom' }
                        content={ projectSubObjective.subObjective.displayName }
                    />
                ) }

                <StyledContainer textAlign={ Align.LEFT } padding={ [ null, null, Size.MD, null ] }>

                    { ( reportableItems.length > 0 ) ? (
                        <List divided size={ 'small' }>

                            { reportableItems.map( ( reportItem, index ) => (
                                <List.Item key={ index }>
                                    <Grid columns={ `30px auto` } columnGap={ '2px' } rowGap={ '0px' }>
                                        <Cell middle center>
                                            <Label size={ 'small' } color={ reportItem.color as SemanticUIColor } circular horizontal>{ reportItem.value }</Label>
                                        </Cell>
                                        <Cell middle>{ reportItem.desc }</Cell>
                                    </Grid>
                                </List.Item>
                            ) ) }

                        </List>

                    ) : (

                          <>{ isComplete && <p><Icon name={ 'check circle' } color={ 'green' }/> Objective Complete</p> }</>
                      ) }

                    <StyledContainer>

                        <Progress size={ 'tiny' } color={ progress.status === OBJECTIVE_STATUS.COMPLETE ? 'green' : 'teal' } percent={ getObjectiveCompletionPercentage( progress ) }>
							<span className={ 'text--no-wrap' }>
							{ !isComplete
                              ? <><strong>{ getObjectiveCompletionOutOfString( progress ) }</strong> Activities Complete</>
                              : <>
                                  <List>
                                      <List.Item><strong>{ getObjectiveStatusCountStatement( progress, ACTIVITY_STATUS.COMPLETE ) }</strong></List.Item>
                                  </List>
                              </>
                            }
							</span>
                        </Progress>

                    </StyledContainer>

                </StyledContainer>

                { props.allowUpdate && (
                    <StyledContainer textAlign={ Align.CENTER }>
                        <Button primary compact size={ 'tiny' } onClick={ handleOpenObjectiveProgressionDialog } content={ 'Update Progress' }/>
                    </StyledContainer>
                ) }

            </div>
        );

    }, [ progress ] );

    /* Pass through component so it can receive progress info */
    let IndicatorComponent: FC<PassThroughComponentProps> = ( p ) => props.indicatorComponent( p.progress, p.isLoading, p.onClick ) ?? <></>;

    /* Pass through component so it can receive progress info */
    let AppendedComponent: FC<PassThroughComponentProps> = ( p ) => props.appendedComponent( p.progress, p.isLoading, p.onClick ) ?? <></>;

    let hasCustomIndicator   = _.isFunction( props.indicatorComponent ),
        hasAppendedComponent = _.isFunction( props.appendedComponent );

    let allActivitiesDeferred = isLoading ? null : ( progress.activities.willNotDo === progress.activities.total );

    return (
        <>
            <Popup content={ isLoading ? null : getPopupContent() }
                   disabled={ isLoading }
                   flowing={ true }
                   closeOnTriggerMouseLeave={ true }
                   closeOnPortalMouseLeave={ true }
                   closeOnTriggerBlur={ true }
                   popperModifiers={ {
                       preventOverflow: {
                           mainAxis:          false,
                           boundariesElement: "window"
                       }
                   } }
                   on={ 'hover' }
                   { ...props.popup }
                   positionFixed={ true }
                   trigger={

                       /* note: Return component must be in a HTMl element ! */
                       <div style={ { minHeight: hasCustomIndicator ? 0 : props.size } }>
                           { hasCustomIndicator
                             ? <IndicatorComponent { ...( { progress, isLoading, onClick: handleOpenObjectiveProgressionDialog } as PassThroughComponentProps ) }/>
                             : ( !isObjectiveCompletelyDeferred( progress ) )

                                 /* Some activities not deferred */
                               ? <CriticalityHarveyBall sectors={ isLoading ? 0 : getObjectiveStatusHarveyBallSectors( progress ) }
                                                        loading={ isLoading }
                                                        size={ props.size }
                                                        completedIconProps={ props.completedIconProps ?? null }
                                                        onClick={ isCorvalManaged ? handleOpenObjectiveProgressionDialog : null }
                                                        content={
                                                            ( props.showPercentageComplete && getObjectiveCompletionPercentage( progress ) < 100 )
                                                            ? <span className={ 'text--xs' }>{ getObjectiveCompletionPercentageString( progress ) }</span>
                                                            : null
                                                        }/>

                                 /* All deferred activities */
                               : <InactiveHarveyBall loading={ isLoading } size={ props.size } onClick={ isCorvalManaged ? handleOpenObjectiveProgressionDialog : null }/>
                           }

                           { hasAppendedComponent &&
                             <AppendedComponent { ...( { progress, isLoading, onClick: handleOpenObjectiveProgressionDialog } as PassThroughComponentProps ) } /> }
                       </div>

                   }/>
        </>
    );

}

ObjectiveProgressionIndicator.defaultProps = defaultProps;

export { ObjectiveProgressionIndicator }
